/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Usage", description = "See which resources across your workspace depend on a model, column, or audience before you change or delete it.", lastUpdatedDate = new Date(1790121600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Usage",
        description: "See which resources across your workspace depend on a model, column, or audience before you change or delete it.",
        lastUpdatedDate: "Wed Sep 23 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Where you see Usage",
          "depth": 0,
          "id": "where-you-see-usage"
        }, {
          "value": "How to understand usage",
          "depth": 0,
          "id": "how-to-understand-usage"
        }, {
          "value": "Direct and indirect dependents",
          "depth": 1,
          "id": "direct-and-indirect-dependents"
        }, {
          "value": "Filter by resource type",
          "depth": 1,
          "id": "filter-by-resource-type"
        }, {
          "value": "How current it is",
          "depth": 1,
          "id": "how-current-it-is"
        }, {
          "value": "What Usage tracks",
          "depth": 0,
          "id": "what-usage-tracks"
        }, {
          "value": "What Usage doesn't track",
          "depth": 0,
          "id": "what-usage-doesnt-track"
        }, {
          "value": "Export usage",
          "depth": 0,
          "id": "export-usage"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "Usage never finishes calculating",
          "depth": 1,
          "id": "usage-never-finishes-calculating"
        }, {
          "value": "A count looks lower than expected",
          "depth": 1,
          "id": "a-count-looks-lower-than-expected"
        }, {
          "value": "A resource you deleted still appears",
          "depth": 1,
          "id": "a-resource-you-deleted-still-appears"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      code: "code",
      img: "img"
    }, _provideComponents(), props.components), {FeatureFlag, PylonLink} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "Usage"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["Completed ", _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "schema setup"
              }), " so there are resources to trace."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Usage shows which resources across your workspace depend on a given model, column, or audience, so you can see what a change would affect before you make it."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "As a workspace grows into hundreds of connected resources, it gets hard to know what depends on what, and understand the impact of making a change. Usage traces those dependencies down to the column level, so you can understand how a resource is used and what a change to it would affect."
      }), "\n", _jsx(_components.p, {
        children: "You open Usage from the resource you're about to change. It answers one question: what else in the workspace depends on this?"
      }), "\n", _jsx(_components.p, {
        children: "Use it to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "plan a change or deletion without breaking an audience, sync, or journey that's still in use"
        }), "\n", _jsx(_components.li, {
          children: "find models, columns, and audiences that nothing depends on, so you can clean up your workspace"
        }), "\n", _jsx(_components.li, {
          children: "check where a column or audience is already used when you're not sure you're building on the right one"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Usage covers dependencies ", _jsx(_components.strong, {
          children: "inside Hightouch"
        }), ". It doesn't trace upstream to your warehouse tables or downstream to individual fields in your destinations."]
      }), "\n", _jsx(_components.h2, {
        id: "where-you-see-usage",
        children: "Where you see Usage"
      }), "\n", _jsx(_components.p, {
        children: "Usage appears on the resources you're most likely to change, so you can check it without leaving your workflow."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Resource"
            }), _jsx(_components.th, {
              children: "Where to find it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Audience"
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.strong, {
                children: "Usage"
              }), " tab on the audience."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Schema model"
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.strong, {
                children: "Usage"
              }), " tab on the model."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Reverse ETL model"
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.strong, {
                children: "Usage"
              }), " tab on the model."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Column"
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.strong, {
                children: "Usage"
              }), " column on the model's ", _jsx(_components.strong, {
                children: "Columns"
              }), " tab. Select a count to open the drawer."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Trait"
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.strong, {
                children: "Usage"
              }), " tab on the trait."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Other resource types, such as syncs and journeys, appear as dependents inside these views even though they don't yet have their own Usage tab."
      }), "\n", _jsx(_components.h2, {
        id: "how-to-understand-usage",
        children: "How to understand usage"
      }), "\n", _jsx(_components.h3, {
        id: "direct-and-indirect-dependents",
        children: "Direct and indirect dependents"
      }), "\n", _jsxs(_components.p, {
        children: ["Usage groups what depends on a resource by how many steps, or hops, away it is. ", _jsx(_components.strong, {
          children: "Total usage"
        }), " counts every distinct resource that depends on the one you're viewing, direct and indirect combined. Each resource is counted once. The resource-type counts show how many audiences, syncs, journeys, and other resources depend on it."]
      }), "\n", _jsx(_components.p, {
        children: "For a column that's used in an audience that's then synced, the chain reads:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "column → audience (direct) → sync (indirect)"
        })
      }), "\n", _jsx(_components.p, {
        children: "The list expands one level at a time, so open a resource to see what depends on it in turn."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "When you expand everything, you may see the same resource, or the same chain\nof dependencies, more than once. That happens when a resource is reachable\nthrough more than one path, for example a column used directly by a sync and\nalso through a trait that the same sync references. Each resource is still\ncounted once in the totals."
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Direct"
          }), " dependents reference the resource itself, one hop away. For a column, that's an audience that filters on it or a sync that maps it."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Indirect"
          }), " dependents reach the resource through another resource, two or more hops away. A sync of that audience is indirect: it doesn't reference the column itself, but it depends on it through the audience."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/lineage/lineage-usage-panel.png",
          alt: "The Usage panel for a column, showing total, direct, and indirect counts and the dependency tree of audiences, syncs, and a journey"
        })
      }), "\n", _jsx(_components.h3, {
        id: "filter-by-resource-type",
        children: "Filter by resource type"
      }), "\n", _jsxs(_components.p, {
        children: ["Select a resource type to narrow the list to dependents of that type. Filtering keeps the resources that connect the chain: if you filter to ", _jsx(_components.strong, {
          children: "Syncs"
        }), " and a sync depends on the resource through an audience, the audience still shows, because it's part of the path to that sync."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/lineage/lineage-filter-by-resource-type.png",
          alt: "The Usage panel filtered by resource type, with Syncs and Journeys selected"
        })
      }), "\n", _jsx(_components.h3, {
        id: "how-current-it-is",
        children: "How current it is"
      }), "\n", _jsx(_components.p, {
        children: "Usage is calculated when you open it, then cached so it loads quickly the next time. The first calculation for a resource can take a moment, especially in a large workspace."
      }), "\n", _jsx(_components.p, {
        children: "When you change a resource, Usage recalculates the next time you open it. For up to two minutes after a change it can show your last completed result while it recalculates, and tells you it's recalculating. If a recent change isn't reflected, wait a moment and reopen the view."
      }), "\n", _jsx(_components.p, {
        children: "A deleted resource stays in Usage as a broken dependency until the next calculation drops it, so you can still see where it was referenced."
      }), "\n", _jsx(_components.h2, {
        id: "what-usage-tracks",
        children: "What Usage tracks"
      }), "\n", _jsx(_components.p, {
        children: "Usage tracks dependencies between the resources you use to model and activate your data:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "schema models (parent, related, and event models) and the columns within them"
        }), "\n", _jsx(_components.li, {
          children: "reverse ETL models and their columns"
        }), "\n", _jsx(_components.li, {
          children: "audiences"
        }), "\n", _jsx(_components.li, {
          children: "syncs and sync templates"
        }), "\n", _jsx(_components.li, {
          children: "journeys"
        }), "\n", _jsx(_components.li, {
          children: "traits and trait templates"
        }), "\n", _jsx(_components.li, {
          children: "subsets"
        }), "\n", _jsx(_components.li, {
          children: "native delivery campaigns"
        }), "\n", _jsx(_components.li, {
          children: "AI Decisioning (AID) resources"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "what-usage-doesnt-track",
        children: "What Usage doesn't track"
      }), "\n", _jsx(_components.p, {
        children: "Usage doesn't include these, so changing one won't appear as a dependency:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "warehouse tables and columns upstream of Hightouch"
        }), "\n", _jsx(_components.li, {
          children: "charts, metrics, dashboards, and reports"
        }), "\n", _jsx(_components.li, {
          children: "event sources, event syncs, and event destinations"
        }), "\n", _jsx(_components.li, {
          children: "identity resolution (IDR) graphs"
        }), "\n", _jsx(_components.li, {
          children: "audience templates"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "export-usage",
        children: "Export usage"
      }), "\n", _jsxs(_components.p, {
        children: ["Usage doesn't have a self-serve export today. If you need the dependency data offline, for example to plan a large migration or cleanup, your Hightouch account team can generate a full-workspace export of the usage graph. Reach out to your Hightouch representative or ", _jsx(PylonLink, {
          pylonText: "Hi, I need a full-workspace export of the usage graph.",
          linkText: "contact support"
        }), " to request one."]
      }), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "usage-never-finishes-calculating",
        children: "Usage never finishes calculating"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The first calculation for a large workspace is still running, or a rebuild is stuck."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Wait a moment and reopen the view. Large workspaces take longer to calculate. If it doesn't resolve, contact your Hightouch account team."]
      }), "\n", _jsx(_components.h3, {
        id: "a-count-looks-lower-than-expected",
        children: "A count looks lower than expected"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Some resource types aren't tracked yet (see ", _jsx(_components.a, {
          href: "#what-usage-doesnt-track",
          children: "What Usage doesn't track"
        }), "), so a dependency that runs only through an untracked resource isn't counted."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Confirm the dependency runs through tracked resource types."]
      }), "\n", _jsx(_components.h3, {
        id: "a-resource-you-deleted-still-appears",
        children: "A resource you deleted still appears"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Usage recalculates on a short delay, so a recently deleted resource can linger as a broken dependency."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Wait a moment and reopen the view. The deleted resource drops out on the next rebuild."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
